import React, { useState, useEffect } from 'react';
import { getWorkflows, linkWorkflow, unlinkWorkflow } from '../services/apiService';
import { Workflow } from '../types';

/**
 * WorkflowManagementPage Component - Manages workflow links for the authenticated user
 * 
 * This component allows users to:
 * - View all workflows currently linked to their account
 * - Link new workflows by entering the workflow ID
 * - See success/error feedback for operations
 */
const WorkflowManagementPage: React.FC = () => {
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [newWorkflowId, setNewWorkflowId] = useState('');
  const [newWorkflowName, setNewWorkflowName] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [unlinkingId, setUnlinkingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    fetchWorkflows();
  }, []);

  const fetchWorkflows = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getWorkflows();
      setWorkflows(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch workflows';
      setError(errorMessage);
      console.error('Error fetching workflows:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newWorkflowId.trim()) {
      setError('Please enter a workflow ID');
      return;
    }
    try {
      setIsSubmitting(true);
      setError(null);
      setSuccessMessage(null);
      await linkWorkflow(newWorkflowId.trim(), newWorkflowName.trim());
      setSuccessMessage('Workflow linked successfully!');
      setNewWorkflowId('');
      setNewWorkflowName('');
      await fetchWorkflows();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to link workflow';
      setError(errorMessage);
      console.error('Error linking workflow:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUnlink = async (workflowId: string, workflowName?: string) => {
    if (!window.confirm(`Are you sure you want to unlink "${workflowName || 'Unnamed Workflow'}"?`)) {
      return;
    }

    try {
      setUnlinkingId(workflowId);
      setError(null);
      setSuccessMessage(null);
      
      await unlinkWorkflow(workflowId);
      
      setSuccessMessage('Workflow unlinked successfully!');
      
      // Refresh the workflows list
      await fetchWorkflows();
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to unlink workflow';
      setError(errorMessage);
      console.error('Error unlinking workflow:', err);
    } finally {
      setUnlinkingId(null);
    }
  };

  // Clear messages after a delay
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 8000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  return (
    <div className="max-w-6xl mx-auto p-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Workflow Management</h1>
      
      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800">{successMessage}</p>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Linked Workflows Section */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Linked Workflows</h2>
        
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-gray-500 text-lg">Loading workflows...</div>
          </div>
        ) : workflows.length === 0 ? (
          <div className="bg-gray-50 border border-gray-200 rounded-md p-6 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No workflows linked</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by linking your first workflow below.</p>
          </div>
        ) : (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <ul className="divide-y divide-gray-200">
              {workflows.map((workflow) => (
                <li key={workflow.vapi_workflow_id} className="px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <div className="w-8 h-8 bg-blue-100 rounded-md flex items-center justify-center">
                            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                            </svg>
                          </div>
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-gray-900">
                            {workflow.name || 'Unnamed Workflow'}
                          </p>
                          <p className="text-sm text-gray-500">
                            ID: {workflow.vapi_workflow_id}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="text-sm text-gray-500">
                        Linked {new Date(workflow.created_at).toLocaleDateString()}
                      </div>
                      <button
                        onClick={() => handleUnlink(workflow.vapi_workflow_id, workflow.name)}
                        disabled={unlinkingId === workflow.vapi_workflow_id}
                        className="inline-flex items-center px-3 py-1 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {unlinkingId === workflow.vapi_workflow_id ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-red-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Unlinking...
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Unlink
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Link New Workflow Section */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Link a New Workflow</h2>
        
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="workflow-id" className="block text-sm font-medium text-gray-700">
                  Workflow ID
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    id="workflow-id"
                    value={newWorkflowId}
                    onChange={(e) => setNewWorkflowId(e.target.value)}
                    placeholder="Enter your workflow ID"
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    disabled={isSubmitting}
                  />
                </div>
                <p className="mt-2 text-sm text-gray-500">
                  You can find your workflow ID in your voice assistant dashboard.
                </p>
              </div>
              <div>
                <label htmlFor="workflow-name" className="block text-sm font-medium text-gray-700">
                  Workflow Name (optional)
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    id="workflow-name"
                    value={newWorkflowName}
                    onChange={(e) => setNewWorkflowName(e.target.value)}
                    placeholder="Give your workflow a name (optional)"
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    disabled={isSubmitting}
                  />
                </div>
                <p className="mt-2 text-sm text-gray-500">
                  This name will help you identify your workflow in the dashboard.
                </p>
              </div>
              
              <div>
                <button
                  type="submit"
                  disabled={isSubmitting || !newWorkflowId.trim()}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Linking...
                    </>
                  ) : (
                    'Link Workflow'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkflowManagementPage;